import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import { resolveMedia, useSettings } from '../../context/SettingsContext';
import { DEFAULT_LOGO } from '../../config/brand';

export default function Navbar() {
  const { settings } = useSettings();
  const { user, logout } = useAuth();
  const { itemsCount } = useCart();
  const { wishlistCount } = useWishlist();
  const location = useLocation();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);

  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [categories, setCategories] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Mega Menu State: tracks which parent category is currently hovered in the mega box
  const [hoveredParentId, setHoveredParentId] = useState(null);
  const [mobileExpandedCatId, setMobileExpandedCatId] = useState(null);

  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    setMobileMenuOpen(false);
    setProfileDropdownOpen(false);
    setNotifDropdownOpen(false);
    setSearchDropdownOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    api.get('/categories?all=true')
      .then((res) => setCategories(res.data || []))
      .catch(() => {});
  }, []);

  // Compute Parent Categories and Subcategories Map
  const parentCategories = useMemo(() => {
    return categories.filter((c) => !c.parent);
  }, [categories]);

  const subcategoriesByParent = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      if (c.parent) {
        const parentId = c.parent._id || c.parent;
        if (!map[parentId]) map[parentId] = [];
        map[parentId].push(c);
      }
    });
    return map;
  }, [categories]);

  // Current active parent category in the mega box
  const activeParent = useMemo(() => {
    if (hoveredParentId) {
      const found = parentCategories.find((p) => p._id === hoveredParentId);
      if (found) return found;
    }
    return parentCategories[0] || null;
  }, [hoveredParentId, parentCategories]);

  const activeSubcategories = useMemo(() => {
    if (!activeParent) return [];
    return subcategoriesByParent[activeParent._id] || [];
  }, [activeParent, subcategoriesByParent]);

  // Live predictive search data fetching as user types
  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) {
      setSearchResults([]);
      setSearchDropdownOpen(false);
      return;
    }

    const timer = setTimeout(() => {
      setIsSearching(true);
      api.get(`/products?search=${encodeURIComponent(query.trim())}&limit=6`)
        .then((res) => {
          setSearchResults(res.data.items || []);
          setSearchDropdownOpen(true);
        })
        .catch(() => {})
        .finally(() => setIsSearching(false));
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchDropdownOpen(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return undefined;
    }

    const fetchNotifications = () => {
      api.get('/notifications/my')
        .then((res) => {
          setNotifications(res.data);
          setUnreadCount(res.data.filter((n) => !n.isRead).length);
        })
        .catch(() => {});
    };

    fetchNotifications();
    window.addEventListener('notifications:changed', fetchNotifications);
    return () => window.removeEventListener('notifications:changed', fetchNotifications);
  }, [user]);

  const openNotification = async (notification) => {
    setNotifications((current) => current.filter((item) => item._id !== notification._id));
    setUnreadCount((current) => Math.max(0, current - (notification.isRead ? 0 : 1)));
    setNotifDropdownOpen(false);
    try {
      await api.patch(`/notifications/${notification._id}/clear`);
      navigate(notification.link || '/notifications');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not open notification');
      window.dispatchEvent(new Event('notifications:changed'));
    }
  };

  const clearAllNotifications = async () => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      await api.patch('/notifications/clear-all');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not clear notifications');
      window.dispatchEvent(new Event('notifications:changed'));
    }
  };

  const submitSearch = (e) => {
    if (e) e.preventDefault();
    if (query.trim()) {
      setSearchDropdownOpen(false);
      navigate(`/shop?search=${encodeURIComponent(query.trim())}`);
    }
  };

  const handleLogout = () => {
    logout();
    setProfileDropdownOpen(false);
    toast.success('Logged out successfully');
    navigate('/');
  };

  const pathname = location.pathname;
  const isPathActive = (path) => {
    if (path === '/') return pathname === '/';
    return pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-40 bg-[#FAF8F5] border-b border-[#E8DFD8] shadow-sm">
      {/* Main Navbar Row */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-2.5 min-h-[56px] flex items-center justify-between gap-2 sm:gap-4 lg:gap-6">
        {/* Brand Logo */}
        <Link to="/" className="group shrink-0" aria-label={`${settings?.siteName || 'Chandana Aura'} home`}>
          <img src={resolveMedia(settings?.logo || DEFAULT_LOGO)} alt={settings?.siteName || 'Chandana Aura'} className="h-9 w-[116px] object-contain object-left sm:h-10 sm:w-[140px] lg:h-11 lg:w-[160px]" />
        </Link>

        {/* Center Live Data Search Bar */}
        <div className="hidden md:flex flex-1 max-w-md relative" ref={searchRef}>
          <form onSubmit={submitSearch} className="w-full relative">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => {
                if (searchResults.length > 0) setSearchDropdownOpen(true);
              }}
              placeholder="Search for products (e.g. Ganesha, Mala, Agarbatti)..."
              className="w-full bg-[#FAF8F5] border border-[#E0D6CC] text-[#2B1810] placeholder-[#8A796C] text-xs px-4 py-1.5 rounded-full focus:outline-none focus:border-[#C49A45] transition-colors pr-16"
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setSearchResults([]);
                  setSearchDropdownOpen(false);
                }}
                className="absolute right-8 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#8A796C] hover:text-[#2B1810] hover:bg-[#E0D6CC]/70 rounded-full transition-colors"
                title="Clear search"
                aria-label="Clear search"
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
            <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A796C] hover:text-[#C49A45]">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
              </svg>
            </button>
          </form>

          {/* Live Data Autocomplete Results Dropdown */}
          {searchDropdownOpen && (
            <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-[#E8DFD8] rounded-2xl shadow-2xl z-50 overflow-hidden text-xs text-[#2B1810] animate-fadeIn">
              {isSearching ? (
                <div className="p-4 text-center text-[#8A796C]">Searching live catalog...</div>
              ) : searchResults.length === 0 ? (
                <div className="p-4 text-center text-[#8A796C]">No products found for "{query}"</div>
              ) : (
                <div>
                  <div className="px-4 py-2 bg-[#FAF8F5] border-b border-[#E8DFD8] text-[10px] font-bold uppercase tracking-wider text-[#C49A45] flex items-center justify-between">
                    <span>Matching Products ({searchResults.length})</span>
                    <span>Live Results</span>
                  </div>

                  <div className="max-h-72 overflow-y-auto divide-y divide-[#F5EFEB]">
                    {searchResults.map((item) => (
                      <Link
                        key={item._id}
                        to={`/product/${item.slug}`}
                        onClick={() => {
                          setSearchDropdownOpen(false);
                          setQuery('');
                        }}
                        className="flex items-center gap-3 p-3 hover:bg-[#FAF8F5] transition-colors group"
                      >
                        <img
                          src={item.images?.[0] || '/assets/hero_perfect_ganesha.jpg'}
                          alt={item.name}
                          className="w-10 h-10 object-cover rounded-lg border border-[#E8DFD8] group-hover:border-[#C49A45]"
                          onError={(e) => {
                            e.target.src = '/assets/hero_perfect_ganesha.jpg';
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-[#2B1810] group-hover:text-[#C49A45] transition-colors truncate">
                            {item.name}
                          </h4>
                          <span className="text-[10px] text-[#8A796C]">
                            {item.category?.name || 'Sandalwood'}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-[#C49A45] text-xs block">₹{item.price}</span>
                          {item.compareAtPrice > item.price && (
                            <span className="text-[10px] text-[#8A796C] line-through">₹{item.compareAtPrice}</span>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>

                  <button
                    onClick={submitSearch}
                    className="w-full text-center px-4 py-2.5 bg-[#F5EFEB] hover:bg-[#E8DFD8] text-[#2B1810] font-bold uppercase tracking-wider text-[10px] border-t border-[#E8DFD8] block"
                  >
                    View All Results for "{query}" →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Icons Right */}
        <div className="flex items-center gap-3 sm:gap-4 lg:gap-5">
          {/* Wishlist Link */}
          <Link
            to="/wishlist"
            className="relative text-[#2B1810] hover:text-[#C49A45] transition-colors"
            aria-label="Wishlist"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            {wishlistCount > 0 && (
              <span className="absolute -top-1.5 -right-2 bg-[#C49A45] text-white font-bold text-[10px] w-4 h-4 rounded-full flex items-center justify-center shadow">
                {wishlistCount > 9 ? '9+' : wishlistCount}
              </span>
            )}
          </Link>

          {/* Cart Link */}
          <Link
            to="/cart"
            className="relative text-[#2B1810] hover:text-[#C49A45] transition-colors"
            aria-label="Shopping Cart"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
            {itemsCount > 0 && (
              <span className="absolute -top-1.5 -right-2 bg-[#C49A45] text-white font-bold text-[10px] w-4 h-4 rounded-full flex items-center justify-center shadow">
                {itemsCount > 9 ? '9+' : itemsCount}
              </span>
            )}
          </Link>

          {/* User Notifications */}
          {user && (
            <div className="relative" ref={notifRef}>
              <button
                type="button"
                onClick={() => {
                  setNotifDropdownOpen(!notifDropdownOpen);
                  setProfileDropdownOpen(false);
                }}
                className="relative text-[#2B1810] hover:text-[#C49A45] transition-colors flex items-center"
                aria-label="Notifications"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-2 bg-red-600 text-white font-bold text-[10px] w-4 h-4 rounded-full flex items-center justify-center shadow animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {notifDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-[#E8DFD8] rounded-2xl shadow-2xl z-50 overflow-hidden text-xs text-[#2B1810] animate-fadeIn">
                  <div className="p-3.5 bg-[#FAF8F5] border-b border-[#E8DFD8] flex items-center justify-between">
                    <span className="font-heading font-bold text-xs uppercase tracking-wider text-[#2B1810]">
                      Notifications {unreadCount > 0 && `(${unreadCount})`}
                    </span>
                    {notifications.length > 0 && (
                      <button
                        type="button"
                        onClick={clearAllNotifications}
                        className="text-[10px] text-red-700 hover:underline font-bold uppercase"
                      >
                        Clear All
                      </button>
                    )}
                  </div>

                  <div className="max-h-64 overflow-y-auto divide-y divide-[#F5EFEB]">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-[#8A796C]">No notifications yet.</div>
                    ) : (
                      notifications.slice(0, 10).map((n) => (
                        <div
                          key={n._id}
                          onClick={() => openNotification(n)}
                          className={`p-3.5 hover:bg-[#FAF8F5] transition-colors cursor-pointer flex gap-3 ${
                            !n.isRead ? 'bg-amber-50/40' : ''
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className={`font-bold text-xs ${!n.isRead ? 'text-[#2B1810]' : 'text-[#6B5B50]'}`}>
                                {n.title}
                              </span>
                              <span className="text-[9px] text-[#8A796C] shrink-0">
                                {new Date(n.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-[11px] text-[#8A796C] mt-0.5 line-clamp-2">{n.message}</p>
                          </div>
                          {!n.isRead && (
                            <span className="w-2 h-2 rounded-full bg-[#C49A45] shrink-0 self-center" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* User Profile / Login Dropdown */}
          <div className="relative" ref={dropdownRef}>
            {user ? (
              <button
                type="button"
                onClick={() => {
                  setProfileDropdownOpen(!profileDropdownOpen);
                  setNotifDropdownOpen(false);
                }}
                className="flex items-center gap-2 text-xs font-bold text-[#2B1810] hover:text-[#C49A45] transition-colors"
                aria-label="User profile menu"
              >
                <div className="w-8 h-8 rounded-full bg-[#C49A45] text-white flex items-center justify-center font-bold shadow-sm">
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <span className="hidden lg:inline-block max-w-[110px] truncate">{user.name?.split(' ')[0]}</span>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={`transition-transform duration-200 ${profileDropdownOpen ? 'rotate-180' : ''}`}>
                  <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#2B1810] hover:text-[#C49A45] transition-colors py-1.5 px-3 rounded-full border border-[#D9D0C8] hover:border-[#C49A45]"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                <span>Login</span>
              </Link>
            )}

            {/* Profile Dropdown Menu */}
            {profileDropdownOpen && user && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-[#E8DFD8] rounded-2xl shadow-2xl z-50 overflow-hidden py-2 text-xs text-[#2B1810] animate-fadeIn">
                <div className="p-3 bg-[#FAF8F5] border-b border-[#E8DFD8]">
                  <p className="font-bold text-xs text-[#2B1810] truncate">{user.name}</p>
                  <p className="text-[10px] text-[#8A796C] truncate">{user.email}</p>
                </div>

                <div className="py-1">
                  {user.accountType === 'admin' && (
                    <Link
                      to="/admin"
                      className="flex items-center gap-2 px-4 py-2 hover:bg-[#FAF8F5] hover:text-[#C49A45] font-bold text-[#C49A45]"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                      <span>Admin Workspace</span>
                    </Link>
                  )}
                  <Link to="/profile" className="block px-4 py-2 hover:bg-[#FAF8F5] hover:text-[#C49A45]">My Account</Link>
                  <Link to="/orders" className="block px-4 py-2 hover:bg-[#FAF8F5] hover:text-[#C49A45]">My Orders</Link>
                  <Link to="/wishlist" className="block px-4 py-2 hover:bg-[#FAF8F5] hover:text-[#C49A45]">My Wishlist</Link>
                </div>

                <div className="p-2 border-t border-[#E8DFD8]">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-md font-bold transition-colors"
                  >
                    Log Out
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Hamburger Toggle */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden p-1.5 text-[#2B1810] hover:text-[#C49A45] transition-colors"
            aria-label="Open menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Navigation Links Row with Luxury Mega Menu Box on Hover */}
      <nav className="hidden md:block bg-white border-t border-[#E8DFD8]/70 shadow-2xs">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-8 text-xs font-bold tracking-widest text-[#2B1810] uppercase relative">
          {/* HOME */}
          <Link
            to="/"
            className={`transition-all py-3 border-b-2 ${
              isPathActive('/') && pathname === '/'
                ? 'text-[#C49A45] border-[#C49A45] font-bold'
                : 'text-[#2B1810] hover:text-[#C49A45] border-transparent'
            }`}
          >
            HOME
          </Link>

          {/* SHOP with Luxury Interactive Mega Box on Hover */}
          <div className="group py-3">
            <Link
              to="/shop"
              className={`hover:text-[#C49A45] flex items-center gap-1.5 transition-all py-1 border-b-2 ${
                pathname.startsWith('/shop') || pathname.startsWith('/product')
                  ? 'text-[#C49A45] border-[#C49A45] font-bold'
                  : 'text-[#2B1810] border-transparent'
              }`}
            >
              <span>SHOP</span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                className="transform group-hover:rotate-180 transition-transform duration-300 ml-0.5 text-current"
              >
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>

            {/* ============================================================== */}
            {/* LUXURY 4-COLUMN MEGA MENU BOX (Refined Typography & Styling) */}
            {/* ============================================================== */}
            {/* ============================================================== */}
            {/* LUXURY 4-COLUMN MEGA MENU BOX (Crisp Modern Sans-Serif Fonts) */}
            {/* ============================================================== */}
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 hidden group-hover:block w-[880px] max-w-[95vw] z-50 animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="bg-white border border-[#E8DFD8] rounded-2xl shadow-2xl overflow-hidden normal-case text-left font-sans">
                {/* Dynamic Category & Subcategory Columns */}
                <div className="p-7 grid grid-cols-4 gap-7 font-sans">
                  {parentCategories.map((parent) => {
                    const subs = subcategoriesByParent[parent._id] || [];

                    return (
                      <div key={parent._id} className="space-y-2.5 font-sans">
                        {/* Parent Category Header */}
                        <Link
                          to={`/shop?category=${parent.slug || parent._id}`}
                          className="font-sans font-bold text-[12.5px] uppercase tracking-wider text-[#1F1916] hover:text-[#C49A45] transition-colors block pb-1.5 border-b border-[#E8DFD8]"
                        >
                          {parent.name}
                        </Link>

                        {/* Subcategory Links */}
                        {subs.length > 0 ? (
                          <div className="space-y-1 pt-0.5 font-sans">
                            {subs.map((sub) => (
                              <Link
                                key={sub._id}
                                to={`/shop?category=${sub.slug || sub._id}`}
                                className="font-sans text-[13.5px] font-medium text-[#4A3B32] hover:text-[#C49A45] hover:translate-x-1 transition-all py-0.5 block"
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <Link
                            to={`/shop?category=${parent.slug || parent._id}`}
                            className="font-sans text-[13px] font-normal text-[#8A796C] hover:text-[#C49A45] transition-colors py-0.5 block"
                          >
                            Explore {parent.name} →
                          </Link>
                        )}
                      </div>
                    );
                  })}

                  {/* Featured Sandalwood Heritage Luxury Image Banner Card */}
                  <Link
                    to="/shop"
                    className="relative rounded-xl overflow-hidden group/banner block border border-[#E8DFD8] shadow-xs aspect-[3/4] max-h-[220px]"
                  >
                    <img
                      src="/assets/mega_menu_banner.jpg"
                      alt="Mysore Sandalwood Handcrafted Heritage"
                      className="w-full h-full object-cover group-hover/banner:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent flex flex-col justify-end p-3.5 text-white">
                      <span className="text-[9px] font-bold uppercase tracking-widest text-[#C49A45] block">
                        ★ MYSORE HERITAGE
                      </span>
                      <h4 className="font-heading font-bold text-xs leading-snug text-white mt-0.5 drop-shadow-xs">
                        100% Pure Sandalwood
                      </h4>
                      <p className="text-[10px] text-amber-100/90 line-clamp-1 mt-0.5">
                        Authentic handcrafted treasures
                      </p>
                      <span className="inline-flex items-center gap-1 text-[10.5px] font-bold text-[#E5C16C] group-hover/banner:text-white transition-colors mt-1.5">
                        <span>Explore Collection</span>
                        <span className="text-xs">→</span>
                      </span>
                    </div>
                  </Link>
                </div>

                {/* Mega Menu Footer Strip */}
                <div className="px-7 py-3 bg-[#FAF8F5] border-t border-[#E8DFD8] flex items-center justify-between font-sans">
                  <div className="flex items-center gap-4 text-xs text-[#6B5B50] font-sans">
                    <span className="flex items-center gap-1.5 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C49A45]" />
                      Authenticity Certificate Included
                    </span>
                    <span className="hidden sm:inline font-normal">· Free Shipping Across India</span>
                  </div>
                  <Link
                    to="/shop"
                    className="font-sans px-4 py-1.5 bg-[#C49A45] hover:bg-[#B08736] text-white text-xs font-bold uppercase tracking-wider rounded-lg shadow-xs transition-colors"
                  >
                    View All Products →
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* ABOUT US */}
          <Link
            to="/about"
            className={`transition-all py-3 border-b-2 ${
              isPathActive('/about')
                ? 'text-[#C49A45] border-[#C49A45] font-bold'
                : 'text-[#2B1810] hover:text-[#C49A45] border-transparent'
            }`}
          >
            ABOUT US
          </Link>

          {/* AUTHENTICITY */}
          <Link
            to="/authenticity"
            className={`transition-all py-3 border-b-2 ${
              isPathActive('/authenticity')
                ? 'text-[#C49A45] border-[#C49A45] font-bold'
                : 'text-[#2B1810] hover:text-[#C49A45] border-transparent'
            }`}
          >
            AUTHENTICITY
          </Link>

          {/* BLOG */}
          <Link
            to="/blog"
            className={`transition-all py-3 border-b-2 ${
              isPathActive('/blog')
                ? 'text-[#C49A45] border-[#C49A45] font-bold'
                : 'text-[#2B1810] hover:text-[#C49A45] border-transparent'
            }`}
          >
            BLOG
          </Link>

          {/* CONTACT US */}
          <Link
            to="/contact"
            className={`transition-all py-3 border-b-2 ${
              isPathActive('/contact')
                ? 'text-[#C49A45] border-[#C49A45] font-bold'
                : 'text-[#2B1810] hover:text-[#C49A45] border-transparent'
            }`}
          >
            CONTACT US
          </Link>
        </div>
      </nav>

      {/* Mobile Menu Drawer with Expandable Category Accordions */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/45"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close navigation menu"
          />
          <div className="absolute right-0 top-0 h-full w-[min(88vw,360px)] bg-[#FAF8F5] shadow-2xl flex flex-col">
            <div className="h-16 px-4 flex items-center justify-between border-b border-[#E8DFD8]">
              <span className="font-heading font-bold text-base text-[#2B1810]">Menu</span>
              <button type="button" onClick={() => setMobileMenuOpen(false)} className="w-10 h-10 flex items-center justify-center" aria-label="Close menu">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <form onSubmit={submitSearch} className="p-4 border-b border-[#E8DFD8] flex gap-2">
              <div className="relative flex-1">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search products"
                  className="input-field min-w-0 w-full pr-8"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery('');
                      setSearchResults([]);
                    }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#8A796C] hover:text-[#2B1810] rounded-full"
                    title="Clear search"
                    aria-label="Clear search"
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
              <button type="submit" className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#2B1810] text-white rounded-md" aria-label="Search">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              </button>
            </form>

            <nav className="flex-1 overflow-y-auto p-3 text-sm font-bold uppercase">
              {[
                ['/', 'Home'], ['/shop', 'Shop'], ['/about', 'About Us'], ['/authenticity', 'Authenticity'], ['/blog', 'Blog'], ['/contact', 'Contact Us'], ['/wishlist', 'Wishlist'], ['/orders', 'My Orders']
              ].map(([to, label]) => (
                <Link key={to} to={to} className={`block px-3 py-3 border-b border-[#E8DFD8] ${isPathActive(to) ? 'text-[#C49A45]' : 'text-[#2B1810]'}`}>
                  {label}
                </Link>
              ))}

              {/* Mobile Hierarchical Categories & Subcategories List */}
              {parentCategories.length > 0 && (
                <div className="pt-4 pb-2">
                  <p className="px-3 pb-2 text-[10px] text-[#8A796C] tracking-wider uppercase font-bold">Categories & Subcategories</p>
                  <div className="space-y-1">
                    {parentCategories.map((parent) => {
                      const subs = subcategoriesByParent[parent._id] || [];
                      const isExpanded = mobileExpandedCatId === parent._id;

                      return (
                        <div key={parent._id} className="border-b border-[#E8DFD8]/60 pb-1">
                          <div className="flex items-center justify-between px-3 py-2">
                            <Link
                              to={`/shop?category=${parent.slug || parent._id}`}
                              className="font-bold text-xs normal-case text-[#2B1810] flex-1 truncate"
                            >
                              {parent.name}
                            </Link>

                            {subs.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setMobileExpandedCatId(isExpanded ? null : parent._id)}
                                className="px-2 py-1 text-[10px] font-bold text-[#8C6B28] bg-amber-50 rounded border border-[#C49A45]/30 ml-2 shrink-0"
                              >
                                {subs.length} {isExpanded ? '▲' : '▼'}
                              </button>
                            )}
                          </div>

                          {/* Expanded Subcategories on Mobile */}
                          {subs.length > 0 && isExpanded && (
                            <div className="pl-6 pr-3 py-1 space-y-1 bg-[#FAF8F5] border-l-2 border-[#C49A45] ml-4 my-1">
                              {subs.map((sub) => (
                                <Link
                                  key={sub._id}
                                  to={`/shop?category=${sub.slug || sub._id}`}
                                  className="block py-1.5 text-xs normal-case text-[#6B5B50] hover:text-[#C49A45]"
                                >
                                  ↳ {sub.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </nav>

            <div className="p-4 border-t border-[#E8DFD8] grid grid-cols-2 gap-2">
              {user ? (
                <>
                  <Link to="/profile" className="btn-outline text-center px-3">My Account</Link>
                  <button type="button" onClick={handleLogout} className="btn-dark px-3">Log Out</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn-outline text-center px-3">Log In</Link>
                  <Link to="/register" className="btn-dark text-center px-3">Register</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
